<template>
  <div class="blog">
    <div class="container background-container">
      <div class="row">
        <div class="col-md-12 p-0 pt-4">
          <img
            class="blog__header-image"
            src="@/assets/images/blog/sophie/header.jpg"
            alt="header-image"
          />
        </div>
      </div>
    </div>

    <div class="container p-md-0">
      <div class="row">
        <div class="col-md-12 p-md-0">
          <h1 class="blog__title">
            Northeast teacher’s new career is the perfect fit
          </h1>
        </div>
      </div>

      <div class="row position-relative">
        <div
          v-if="userAgent && !isInstagramBrowser"
          class="blog__social d-md-flex d-none"
        >
          <span class="blog__social-title">Share</span>
          <div
            v-for="(link, key) in socialLinks"
            :key="key"
            class="mt-2"
            @click="handleShareClick"
          >
            <img :src="getImageItem(key)" :alt="key" />
          </div>
        </div>
        <div class="col-md-8 offset-md-2">
          <p>
            A PRIMARY school teacher has given up her job to pursue her passion
            for promoting health and fitness.
          </p>
          <p>
            And the success of Newton Aycliffe-based Sophie Clothier in building
            an international client base has led to the publication of her first
            cookbook.
          </p>
          <p>
            Sophie set up her online healthy eating and exercise platform
            -fitfooddiary_x – to inspire people to get in shape, as well as
            creating a whole host of tasty recipes.
          </p>

          <p>
            And now her success has seen the unveiling of Fit Food Diary,
            featuring many of Sophie’s delicious and easy-to-follow recipes.
          </p>

          <img
            class="blog__landscape-image"
            src="@/assets/images/blog/sophie/2.jpg"
            alt="image"
          />

          <p>
            The book is now available to order, which a very excited Sophie is
            delighted about.
          </p>

          <div class="blog__quote my-5">
            <p>
              “I can’t believe I’m going to have my own cookery book,” she said.
            </p>
          </div>

          <p>
            “It’ looks absolutely fantastic and hopefully it will help inspire
            other people to try out some of the recipes.”
          </p>
          <p>
            Sophie, 31, was a teacher for seven years but decided to make the
            leap into a new career.
          </p>
          <p>
            “I’ve always been very interested in fitness, and I qualified as a
            personal trainer,” she said.
          </p>
          <p>
            “I’ve managed to build up a really strong client base, with some of
            them as far away as Dubai.
          </p>
          <p>
            “I decided last year that this is what I wanted to do so I gave up
            my teaching job so I could focus on the fitness full time.”
          </p>
        </div>

        <div class="col-md-8 offset-md-2">
          <div class="blog__double-image">
            <img src="@/assets/images/blog/sophie/3.jpg" alt="image" />
            <img src="@/assets/images/blog/sophie/4.jpg" alt="image" />
          </div>
        </div>

        <div class="col-md-8 offset-md-2 mt-3">
          <p>
            Sophie’s book has been published through Found, an innovative system
            which helps influencers turn then content into cash – with
            absolutely no outlay from them.
          </p>
          <p>
            Found turns the recipes into stylish books with the influencer’s
            only obligation to promote them via their platforms, for which they
            receive a percentage of each one sold.
          </p>
          <p>
            She hopes her own journey – including her own weight loss – will
            help inspire others.
          </p>
          <p>
            “You can’t deprive yourself of everything you like to eat so my
            recipes include all kinds of food which I hope will inspire people,”
            she said.
          </p>
          <p>
            “I try to create content which is aimed at showing how you can
            improve your fitness and I’m glad that I’ve built a really nice
            community.
          </p>
          <p>
            “The book is the next step on that journey, and I am so excited
            about it.”
          </p>

          <div
            v-if="userAgent && !isInstagramBrowser"
            class="blog__social d-md-none"
          >
            <span class="blog__social-title">Share</span>
            <div class="d-flex">
              <div
                v-for="(link, key) in socialLinks"
                :key="key"
                class="mt-2 mr-md-0 mr-3"
                @click="handleShareClick"
              >
                <img :src="getImageItem(key)" :alt="key" />
              </div>
            </div>
          </div>

          <div v-if="tags && tags.length">
            <h2 class="mt-4">Related topics</h2>
            <div class="blog__tags">
              <Tag
                v-for="tag in tags"
                :key="tag"
                class="mr-3 my-2"
                @click="onTagClick(tag)"
              >
                {{ tag }}
              </Tag>
            </div>
          </div>

          <div class="blog__action-button mt-5">
            <Button
              class="pt-2"
              full-width
              size="sm"
              @click="handleBeFoundClick"
            >
              I’m ready to talk
            </Button>
          </div>

          <div class="blog__action-button mt-3 mb-5">
            <Button
              class="pt-2"
              full-width
              size="sm"
              variant="outline"
              @click="$router.push('/blog')"
            >
              View all blogs
            </Button>
          </div>

          <Carousel
            v-if="creatorProducts && creatorProducts.length"
            id="blog-products"
            class="my-4"
            :items="creatorProducts"
            :mode="CarouselModes.PRODUCT"
            show-default-item-actions
            @click="handleProductBundleClick"
          >
            <template #title>
              <h2>More from {{ creatorHandle }}</h2>
            </template>
          </Carousel>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { throttle } from 'lodash';
import socialLinks from '@/misc/socialLinks';
import { CarouselModes, WebApiCancelError } from '@/misc/constants';
import Tag from '@/components/Elements/Tag.vue';
import Button from '@/components/Elements/Button.vue';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import formatMoney from '@/misc/formatMoney';
import getFormatPrice from '@/misc/getFormatPrice';
import { fetchProductsByCreator } from '@/services/products';
import filterProductsByRegion from '@/misc/filterProductByRegion';

export default {
  name: 'BlogItemPage',
  components: {
    Button,
    Tag,
    Carousel: () =>
      import(/* webpackPrefetch: true */ '@/components/Carousel/Index')
  },

  data() {
    return {
      creatorHandle: 'fitfooddiary_x',
      CarouselModes,
      socialLinks,
      creatorProducts: [],
      tags: ['Creator Stories']
    };
  },

  head: {
    title: 'Be Found | Blog'
  },

  computed: {
    ...mapState('main', ['userAgent']),
    ...mapGetters({
      isInstagramBrowser: 'main/isInstagramBrowser',
      currency: 'config/currency',
      region: 'config/region'
    })
  },

  mounted() {
    fetchProductsByCreator(this.$axios, this.creatorHandle)
      .then((response) => {
        const creatorProducts = this.$get(response, 'creatorProducts');
        this.creatorProducts = filterProductsByRegion(
          creatorProducts,
          this.region
        );
        this.contributedProducts = filterProductsByRegion(
          this.$get(response, 'contributedProducts'),
          this.region
        );
      })
      .catch((err) => {
        this.$showToastMessage(
          this.$get(err, 'response.data.message'),
          'Error',
          'danger'
        );
      });
  },

  methods: {
    getImageItem(key) {
      return require(`@/assets/icons/social/${key}.svg`);
    },

    onTagClick(tag) {
      this.$router.push('/blog?filter=' + tag);
    },

    handleBeFoundClick() {
      this.$router.push({ path: '/be-found' });
    },

    handleShareClick: throttle(function () {
      const url = window.location.href;
      if (navigator?.share) {
        navigator.share({ url }).catch((err) => {
          if (!err.message.includes(WebApiCancelError)) {
            this.$showToastMessage(err.message, 'Error', 'error');
          }
        });
      } else if (navigator?.clipboard) {
        navigator.clipboard
          .writeText(url)
          .then(() =>
            this.$showToastMessage('Link is copied', 'Success', 'success')
          )
          .catch((err) =>
            this.$showToastMessage(
              `Failed to copied link ${err.message}`,
              'Error',
              'error'
            )
          );
      } else {
        this.$showToastMessage(
          `Sharing function is not supported`,
          'Error',
          'error'
        );
      }
    }, 1500),

    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },

    handleProductBundleClick(product) {
      this.$router.push({
        path: `/products/${product.id || product.item.id}`
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.blog {
  &__header-image {
    object-fit: cover;
    height: 480px;
    width: 100%;
  }

  &__title {
    padding-top: 40px;
    text-align: left;
    line-height: 56px;
  }

  &__landscape-image {
    object-fit: cover;
    transform: translate3d(0, 0, 1px);
    height: 550px;
    width: 100%;
    margin-bottom: 40px;
  }

  &__social {
    position: absolute;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    text-align: center;

    &-title {
      font-size: 12px;
    }
  }

  &__double-image {
    display: flex;
    justify-content: space-around;

    img {
      object-fit: cover;
      height: 700px;
      &:first-child {
        margin-right: 20px;
      }
    }
  }

  &__quote {
    font-family: 'Poppins-SemiBold', sans-serif;
    font-size: 24px;
    border-left: 8px solid $primary-color;
    padding-left: 35px;
  }

  &__action-button {
    margin: 0 auto;
    max-width: 240px;
  }

  @media (max-width: $screen-width-md) {
    &__header-image {
      object-fit: contain;
      height: 242px;
    }

    &__landscape-image {
      height: 328px;
    }

    &__double-image {
      flex-wrap: wrap;

      img {
        margin-bottom: 25px;
        object-fit: cover;
        height: 420px;
        &:first-child {
          margin-right: 0;
        }
      }
    }

    &__social {
      position: initial;
      flex-direction: column;
      text-align: left;
    }
  }
}
</style>
